import { useEffect, useMemo, useState } from 'react';
import { Activity, LoaderCircle } from 'lucide-react';
import type { EditorDocument } from '../core/model';
import { Canvas2DRenderer } from '../render/renderer';
import { computeHistogram } from '../workers/analysis-client';

interface HistogramPanelProps {
  document: EditorDocument;
  revision: number;
}

function channelPath(channel: Float64Array, max: number): string {
  return [...channel].map((value, index) => {
    const x = (index / 255) * 240;
    const normalized = max <= 0 ? 0 : Math.log1p(value) / Math.log1p(max);
    const y = 92 - normalized * 88;
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
}

export function HistogramPanel({ document, revision }: HistogramPanelProps) {
  const renderer = useMemo(() => new Canvas2DRenderer(), []);
  const [channels, setChannels] = useState<Float64Array | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setChannels(null);
    setError('');
    const timeout = window.setTimeout(() => {
      try {
        const scale = Math.min(1, 512 / document.width, 512 / document.height);
        const canvas = renderer.flatten(document, scale);
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Canvas analysis is unavailable.');
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        void computeHistogram(new Uint8ClampedArray(pixels), controller.signal)
          .then(setChannels)
          .catch((reason: unknown) => {
            if (!(reason instanceof DOMException && reason.name === 'AbortError')) setError(reason instanceof Error ? reason.message : 'Histogram failed.');
          });
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Histogram failed.');
      }
    }, 180);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [document, renderer, revision]);

  const red = channels?.subarray(0, 256) ?? new Float64Array(256);
  const green = channels?.subarray(256, 512) ?? new Float64Array(256);
  const blue = channels?.subarray(512, 768) ?? new Float64Array(256);
  const luminance = channels?.subarray(768, 1024) ?? new Float64Array(256);
  const max = Math.max(1, ...red, ...green, ...blue, ...luminance);

  return <div className="histogram-panel">
    <div className="histogram-heading"><span><Activity size={14} /> RGB histogram</span><em>Worker analyzed · logarithmic</em></div>
    <div className="histogram-chart">
      {!channels && !error ? <div className="histogram-loading"><LoaderCircle size={18} /> Analyzing composite…</div> : null}
      {error ? <p>{error}</p> : null}
      {channels ? <svg viewBox="0 0 240 96" role="img" aria-label="RGB and luminance histogram">
        <path className="hist-luminance" d={`${channelPath(luminance, max)} L240,96 L0,96 Z`} />
        <path className="hist-red" d={channelPath(red, max)} />
        <path className="hist-green" d={channelPath(green, max)} />
        <path className="hist-blue" d={channelPath(blue, max)} />
      </svg> : null}
    </div>
    <div className="histogram-scale"><span>Shadows</span><span>Midtones</span><span>Highlights</span></div>
    <p>Computed from a bounded composite preview. Transparent pixels are excluded; source tiles remain untouched.</p>
  </div>;
}
